import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import PageMeta from '@/components/base/PageMeta';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteInfo } from '@/mocks/siteInfo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const articles = getArticlesByCategory(currentCategory.id);

  return (
    <>
      <PageMeta
        title={`${currentCategory.name} - 爱情岛 | ${currentCategory.description.slice(0, 30)}`}
        description={`爱情岛${currentCategory.name}栏目：${currentCategory.description}。已收录${currentCategory.articleCount}篇高质量攻略，为情侣提供最实用的旅行建议。`}
        keywords={`爱情岛${currentCategory.name},情侣${currentCategory.name},${currentCategory.name}推荐`}
        canonicalUrl={`${siteInfo.url}/category/${currentCategory.slug}`}
      />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
          { '@type': 'ListItem', position: 2, name: currentCategory.name },
        ],
      }} />

      <SchemaOrg type="WebPage" data={{
        name: `${currentCategory.name} - ${siteInfo.name}`,
        url: `${siteInfo.url}/category/${currentCategory.slug}`,
        description: currentCategory.description,
        isPartOf: { '@type': 'WebSite', name: siteInfo.name, url: siteInfo.url },
      }} />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-20 md:pt-24 pb-10 md:pb-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory.name },
                ]}
              />

              <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
                <div className="flex-shrink-0">
                  <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
                    <i className={`${currentCategory.icon} text-2xl md:text-3xl`}></i>
                  </span>
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-900 mb-2">
                    {currentCategory.name}
                  </h1>
                  <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
                    {currentCategory.description}
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <span className="text-3xl md:text-4xl font-heading font-semibold text-primary-500">{currentCategory.articleCount}</span>
                  <span className="text-sm text-foreground-400 ml-1">篇攻略</span>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-2">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {articles.map((article) => (
                  <article key={article.id} className="bg-background-100 rounded-lg overflow-hidden group cursor-pointer">
                    <Link to={`/article/${article.id}`}>
                      <div className="h-48 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    </Link>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs text-foreground-400 mb-3 flex-wrap">
                        <span>{article.date}</span>
                        <span>·</span>
                        <span>{article.readTime} 分钟阅读</span>
                      </div>
                      <Link to={`/article/${article.id}`}>
                        <h3 className="text-lg font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                          {article.title}
                        </h3>
                      </Link>
                      <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line text-sm"></i>
                          {article.viewCount.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-heart-line text-sm"></i>
                          {article.likeCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="text-foreground-400 text-lg mb-3">该分类暂无文章</div>
                <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm font-medium whitespace-nowrap">
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}